import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtDate, fmtNum } from "@/lib/format";
import { PageHeader } from "@/components/common/Page";
import { DataTable } from "@/components/common/DataTable";
import { KpiCard } from "@/components/common/KpiCard";
import { FormDialog } from "@/components/common/FormDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useProducts, useCustomers, productOptions, customerOptions, nameOf, opt } from "@/lib/lookups";

export default function Labour() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: rows } = useQuery({ queryKey: ["labour"], queryFn: async () => (await api.get("/labour")).data });
  const { data: products } = useProducts();
  const { data: customers } = useCustomers();

  const save = useMutation({
    mutationFn: async (p) => api.post("/labour", p),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Labour entry saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/labour/${id}`), onSuccess: () => { qc.invalidateQueries(); toast.success("Entry removed"); } });

  const list = rows || [];
  const totalCost = list.reduce((s, r) => s + Number(r.cost || 0), 0);
  const billableHours = list.filter((r) => r.billable).reduce((s, r) => s + Number(r.hours || 0), 0);
  const totalHours = list.reduce((s, r) => s + Number(r.hours || 0), 0);

  const fields = [
    { name: "date", label: "Date", type: "date" },
    { name: "person", label: "Person", default: "Founder" },
    { name: "hours", label: "Hours", type: "number", step: "0.25" },
    { name: "activity", label: "Activity", type: "select", options: opt(["development", "support", "onboarding", "sales", "admin", "meeting", "other"]), default: "development" },
    { name: "product_id", label: "Product", type: "select", options: productOptions(products) },
    { name: "customer_id", label: "Customer", type: "select", options: customerOptions(customers) },
    { name: "rate_type", label: "Rate Type", type: "select", options: [
      { value: "internal", label: "Internal cost" }, { value: "commercial", label: "Commercial rate" }], default: "internal" },
    { name: "billable", label: "Billable", type: "switch" },
    { name: "notes", label: "Notes", type: "textarea", full: true },
  ];

  const columns = [
    { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
    { key: "person", header: "Person" },
    { key: "activity", header: "Activity", render: (r) => <span className="capitalize">{r.activity}</span> },
    { key: "product_id", header: "Product", render: (r) => nameOf(products, r.product_id) },
    { key: "customer_id", header: "Customer", render: (r) => nameOf(customers, r.customer_id) },
    { key: "hours", header: "Hours", align: "right", mono: true, render: (r) => fmtNum(r.hours) },
    { key: "rate", header: "Rate", align: "right", mono: true, render: (r) => fmtMoney(r.rate) },
    { key: "cost", header: "Cost", align: "right", mono: true, render: (r) => fmtMoney(r.cost) },
    { key: "billable", header: "Billable", render: (r) => <span className="text-xs text-slate-400">{r.billable ? "Yes" : "No"}</span> },
    { key: "actions", header: "", align: "right", render: (r) => (
      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-400" onClick={() => del.mutate(r.id)} data-testid={`delete-labour-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
    ) },
  ];

  return (
    <div className="ns-fade">
      <PageHeader title="Labour" subtitle="Founder time is not free — track labour cost per product and customer" testId="labour-header">
        <Button onClick={() => setOpen(true)} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" data-testid="add-labour-button">
          <Plus className="mr-1.5 h-4 w-4" /> Log Time
        </Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testId="labour-total-cost" label="Total Labour Cost" value={fmtMoney(totalCost)} accent="amber" />
        <KpiCard testId="labour-total-hours" label="Total Hours" value={fmtNum(totalHours)} accent="cyan" />
        <KpiCard testId="labour-billable-hours" label="Billable Hours" value={fmtNum(billableHours)} accent="emerald" />
        <KpiCard testId="labour-nonbillable" label="Non-billable Hours" value={fmtNum(totalHours - billableHours)} accent="slate" />
      </div>
      <DataTable columns={columns} rows={rows} testId="labour-table" empty={{ title: "No time logged", message: "Track founder and contractor hours to see true cost-to-serve." }} />
      <FormDialog open={open} onOpenChange={setOpen} title="Log Time" fields={fields} onSubmit={(p) => save.mutateAsync(p).then(() => true).catch(() => false)} testId="labour-form" />
    </div>
  );
}
